import useSQLite, { tableExists } from '../useSQLite'
import { defineSqliteFeature } from './define-feature'

const GPS_TTL_SEC = 30 * 86400
const NOOP = async () => {}
const { dbRun, dbQuery, dbExecute } = useSQLite()

const SCHEMA_SQL = `
PRAGMA foreign_keys = ON;
CREATE TABLE IF NOT EXISTS gps_sessions (
    id INTEGER PRIMARY KEY AUTOINCREMENT,
    started_at INTEGER NOT NULL,
    ended_at INTEGER,
    horse_id INTEGER,
    rider_id INTEGER,
    discipline TEXT
);
CREATE TABLE IF NOT EXISTS gps_points (
    id INTEGER PRIMARY KEY AUTOINCREMENT,
    session_id INTEGER NOT NULL,
    timestamp INTEGER NOT NULL,
    latitude REAL NOT NULL,
    longitude REAL NOT NULL,
    accuracy REAL,
    altitude REAL,
    altitude_accuracy REAL,
    speed REAL,
    heading REAL,
    FOREIGN KEY(session_id) REFERENCES gps_sessions(id) ON DELETE CASCADE
);
CREATE INDEX IF NOT EXISTS idx_gps_points_session ON gps_points(session_id);
`

let schemaReady = false
let schemaPromise = null

export const ensureGpsRecordingSchema = async () => {
    if (schemaReady) return
    if (schemaPromise) return schemaPromise
    schemaPromise = dbExecute(SCHEMA_SQL)
        .then(() => {
            schemaReady = true
        })
        .catch((error) => {
            throw error
        })
        .finally(() => {
            schemaPromise = null
        })
    return schemaPromise
}

export const createGpsRecordingSession = async ({ horseId, riderId, discipline } = {}) => {
    await ensureGpsRecordingSchema()
    const startedAt = Date.now()
    const result = await dbRun(
        'INSERT INTO gps_sessions (started_at, horse_id, rider_id, discipline) VALUES (?, ?, ?, ?);',
        [startedAt, horseId ?? null, riderId ?? null, discipline ?? null]
    )
    const lastId = result?.changes?.lastId
    if (lastId) return { id: lastId, startedAt }
    const row = await dbQuery('SELECT last_insert_rowid() as id;')
    return { id: row?.values?.[0]?.id, startedAt }
}

export const endGpsRecordingSession = async (sessionId) => {
    await ensureGpsRecordingSchema()
    const endedAt = Date.now()
    await dbRun('UPDATE gps_sessions SET ended_at = ? WHERE id = ?;', [endedAt, sessionId])
    return endedAt
}

export const insertGpsRecordingPoint = async (sessionId, position) => {
    await ensureGpsRecordingSchema()
    const { coords = {}, timestamp } = position || {}
    await dbRun(
        `INSERT INTO gps_points
        (session_id, timestamp, latitude, longitude, accuracy, altitude, altitude_accuracy, speed, heading)
        VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?);`,
        [
            sessionId,
            timestamp,
            coords.latitude,
            coords.longitude,
            coords.accuracy ?? null,
            coords.altitude ?? null,
            coords.altitudeAccuracy ?? null,
            coords.speed ?? null,
            coords.heading ?? null,
        ]
    )
}

export const fetchGpsRecordingPoints = async (sessionId) => {
    await ensureGpsRecordingSchema()
    const result = await dbQuery(
        `SELECT timestamp, latitude, longitude, accuracy, altitude, altitude_accuracy, speed, heading
         FROM gps_points WHERE session_id = ? ORDER BY timestamp ASC;`,
        [sessionId]
    )
    return result?.values || []
}

const getGpsRecordingSummary = async ({ dbQuery }) => {
    const hasSessions = await tableExists('gps_sessions').catch(() => false)
    const hasPoints = await tableExists('gps_points').catch(() => false)

    const summary = {
        sessionsCount: 0,
        pointsCount: 0,
        oldestSessionAt: null,
        newestSessionAt: null,
        oldestPointAt: null,
        newestPointAt: null,
        now: Date.now(),
    }

    if (hasSessions) {
        const sessionsResult = await dbQuery(
            `SELECT COUNT(*) as count,
                    MIN(COALESCE(ended_at, started_at)) as oldest,
                    MAX(COALESCE(ended_at, started_at)) as newest
             FROM gps_sessions;`
        )
        const row = sessionsResult?.values?.[0] || {}
        summary.sessionsCount = Number(row.count) || 0
        summary.oldestSessionAt = Number(row.oldest) || null
        summary.newestSessionAt = Number(row.newest) || null
    }

    if (hasPoints) {
        const pointsResult = await dbQuery(
            `SELECT COUNT(*) as count, MIN(timestamp) as oldest, MAX(timestamp) as newest
             FROM gps_points;`
        )
        const row = pointsResult?.values?.[0] || {}
        summary.pointsCount = Number(row.count) || 0
        summary.oldestPointAt = Number(row.oldest) || null
        summary.newestPointAt = Number(row.newest) || null
    }

    return summary
}

const pruneGpsRecording = async ({ dbQuery, dbRun, maxAgeSec } = {}) => {
    const ttl = Number(maxAgeSec)
    if (!Number.isFinite(ttl) || ttl <= 0) {
        return { ok: false, sessionsCleared: 0, pointsCleared: 0 }
    }

    const hasSessions = await tableExists('gps_sessions').catch(() => false)
    const hasPoints = await tableExists('gps_points').catch(() => false)
    if (!hasSessions && !hasPoints) {
        return { ok: true, sessionsCleared: 0, pointsCleared: 0, skipped: true }
    }

    const cutoffMs = Date.now() - Math.floor(ttl) * 1000
    let sessionsCleared = 0
    let pointsCleared = 0

    if (hasSessions) {
        const sessionsToDelete = await dbQuery(
            `SELECT COUNT(*) as count
             FROM gps_sessions
             WHERE COALESCE(ended_at, started_at) < ?;`,
            [cutoffMs]
        )
        sessionsCleared = Number(sessionsToDelete?.values?.[0]?.count) || 0

        if (hasPoints && sessionsCleared > 0) {
            const pointsForOldSessions = await dbQuery(
                `SELECT COUNT(*) as count
                 FROM gps_points
                 WHERE session_id IN (
                    SELECT id FROM gps_sessions WHERE COALESCE(ended_at, started_at) < ?
                 );`,
                [cutoffMs]
            )
            pointsCleared += Number(pointsForOldSessions?.values?.[0]?.count) || 0
            await dbRun(
                `DELETE FROM gps_points
                 WHERE session_id IN (
                    SELECT id FROM gps_sessions WHERE COALESCE(ended_at, started_at) < ?
                 );`,
                [cutoffMs]
            )
        }

        if (sessionsCleared > 0) {
            await dbRun(
                `DELETE FROM gps_sessions
                 WHERE COALESCE(ended_at, started_at) < ?;`,
                [cutoffMs]
            )
        }
    }

    if (hasPoints) {
        const orphanPoints = await dbQuery(
            `SELECT COUNT(*) as count
             FROM gps_points
             WHERE session_id NOT IN (SELECT id FROM gps_sessions);`
        )
        const orphanCount = Number(orphanPoints?.values?.[0]?.count) || 0
        if (orphanCount > 0) {
            await dbRun(
                `DELETE FROM gps_points
                 WHERE session_id NOT IN (SELECT id FROM gps_sessions);`
            )
            pointsCleared += orphanCount
        }
    }

    return { ok: true, sessionsCleared, pointsCleared }
}

const clearGpsRecording = async ({ dbQuery, dbRun }) => {
    const hasSessions = await tableExists('gps_sessions').catch(() => false)
    const hasPoints = await tableExists('gps_points').catch(() => false)
    if (!hasSessions && !hasPoints) {
        return { ok: true, sessionsCleared: 0, pointsCleared: 0, skipped: true }
    }

    let sessionsCleared = 0
    let pointsCleared = 0

    if (hasSessions) {
        const sessionsResult = await dbQuery('SELECT COUNT(*) as count FROM gps_sessions;')
        sessionsCleared = Number(sessionsResult?.values?.[0]?.count) || 0
    }

    if (hasPoints) {
        const pointsResult = await dbQuery('SELECT COUNT(*) as count FROM gps_points;')
        pointsCleared = Number(pointsResult?.values?.[0]?.count) || 0
    }

    if (hasPoints && pointsCleared > 0) {
        await dbRun('DELETE FROM gps_points;')
    }
    if (hasSessions && sessionsCleared > 0) {
        await dbRun('DELETE FROM gps_sessions;')
    }

    return { ok: true, sessionsCleared, pointsCleared }
}

export default defineSqliteFeature({
    key: 'gpsRecording',
    ensureSchema: NOOP,
    onBoot: ({ dbQuery, dbRun }) => pruneGpsRecording({ dbQuery, dbRun, maxAgeSec: GPS_TTL_SEC }),
    getSummary: ({ dbQuery }) => getGpsRecordingSummary({ dbQuery }),
    purge: ({ dbQuery, dbRun }) => clearGpsRecording({ dbQuery, dbRun }),
    fallbacks: {
        onBoot: { ok: false, sessionsCleared: 0, pointsCleared: 0 },
        getSummary: null,
        purge: { ok: false, sessionsCleared: 0, pointsCleared: 0 },
    },
})
